import { useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useAuth } from "../context/auth";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useToast } from "../context/toast";
import {} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  NoRowActions,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
} from "../components/ui";
import { TextField } from "../components/ui/form";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { AdminSession, Paged, SessionStatus } from "../types/api";

const STATUS_TONE: Record<
  SessionStatus,
  "success" | "warning" | "neutral" | "danger"
> = {
  active: "success",
  idle: "warning",
  expired: "neutral",
  revoked: "danger",
};

/**
 * Sessions.
 *
 * Tokens used to be self-contained and unrevocable — a password change left
 * every one of them working until it expired. Each sign-in now opens a row here,
 * and ending it refuses the holder's next request.
 */
export function Sessions() {
  const dateTime = useDateTime();
  const relative = useRelative();
  const { can, admin: me } = useAuth();
  const t = useT();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters(
    { q: "", principalType: "", status: "active" },
    () => setPage(1),
  );
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const sessions = useLive<Paged<AdminSession>>("sessions", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        q: filters.applied.q || undefined,
        principalType: filters.applied.principalType || undefined,
        status: filters.applied.status || undefined,
      });

  const [revoking, setRevoking] = useState<AdminSession | null>(null);

  return (
    <div className="space-y-5">
      <div
        className="panel px-5 py-3"
        style={{ borderLeft: "3px solid var(--accent)" }}
      >
        <p className="text-muted text-[12px]">{t("sessions.note")}</p>
      </div>

      <FilterBar
        loading={sessions.loading}
        state={filters}
        filters={[
          {
            key: "q",
            label: t("common.username"),
            placeholder: t("common.username"),
          },
          {
            key: "principalType",
            label: t("sessions.kind"),
            type: "select",
            options: [
              { value: "", label: t("sessions.anyKind") },
              { value: "admin", label: t("sessions.admin") },
              { value: "player", label: t("sessions.player") },
            ],
          },
          {
            key: "status",
            label: t("common.status"),
            type: "select",
            options: [
              { value: "", label: t("common.anyStatus") },
              { value: "active", label: t("status.active") },
              { value: "idle", label: t("status.idle") },
              { value: "expired", label: t("status.expired") },
              { value: "revoked", label: t("status.revoked") },
            ],
          },
        ]}
      />

      <Panel
        eyebrow={t("sessions.eyebrow")}
        title={t("sessions.title")}
        bodyClassName=""
        actions={
          <Button size="sm" onClick={sessions.reload}>
            {t("action.refresh")}
          </Button>
        }
      >
        {sessions.error ? (
          <div className="p-5">
            <ErrorNote message={sessions.error} onRetry={sessions.reload} />
          </div>
        ) : (
          <>
            <Table
              minWidth="62rem"
              head={[
                <SortHeader key="u" field="username" state={sort}>
                  {t("sessions.principal")}
                </SortHeader>,
                t("sessions.kind"),
                t("common.status"),
                <SortHeader key="c" field="createdAt" state={sort}>
                  {t("sessions.startedAt")}
                </SortHeader>,
                <SortHeader key="l" field="lastSeenAt" state={sort}>
                  {t("common.lastSeen")}
                </SortHeader>,
                <SortHeader key="e" field="expiresAt" state={sort}>
                  {t("sessions.expiresAt")}
                </SortHeader>,
                t("sessions.ip"),
                <span key="a" className="block text-right">
                  {t("col.actions")}
                </span>,
              ]}
            >
              {sessions.loading ? (
                <LoadingRows columns={8} />
              ) : sessions.data && sessions.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <EmptyState
                      title={t("sessions.empty")}
                      detail={t("sessions.emptyDetail")}
                    />
                  </Td>
                </Tr>
              ) : (
                (sessions.data?.items ?? []).map((session) => {
                  const isMine =
                    session.principalId === me?.id &&
                    session.status === "active";
                  return (
                    <Tr key={session.id}>
                      <Td>
                        {session.principalType === "player" &&
                        session.principalId ? (
                          <Link
                            to={`/players/${session.principalId}`}
                            className="font-semibold hover:underline"
                          >
                            {session.username}
                          </Link>
                        ) : (
                          <span className="font-semibold">
                            {session.username}
                          </span>
                        )}
                        {isMine ? (
                          <span className="text-dim ml-2 text-[11px]">
                            {t("draws.you")}
                          </span>
                        ) : null}
                      </Td>
                      <Td>
                        <Badge
                          tone={
                            session.principalType === "admin"
                              ? "gold"
                              : "neutral"
                          }
                        >
                          {session.principalType === "admin"
                            ? t("sessions.admin")
                            : t("sessions.player")}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge tone={STATUS_TONE[session.status]}>
                          {t(`status.${session.status}`)}
                        </Badge>
                        {/* "Revoked" on its own does not say who did it. An
                            operator ending a session and the account signing in
                            elsewhere are the same badge and very different
                            events — the second is not staff activity at all. */}
                        {session.revokedReason ? (
                          <p className="text-dim mt-1 text-[11px]">
                            {t(`sessionEnd.${session.revokedReason}` as TranslationKey)}
                          </p>
                        ) : null}
                      </Td>
                      <Td
                        className="text-dim text-[12px]"
                        title={dateTime(session.createdAt)}
                      >
                        {relative(session.createdAt)}
                      </Td>
                      <Td className="text-dim text-[12px]">
                        {relative(session.lastSeenAt)}
                      </Td>
                      <Td className="text-dim text-[12px]">
                        {relative(session.expiresAt)}
                      </Td>
                      <Td className="money text-dim text-[11px]">
                        {session.ip ?? "—"}
                      </Td>
                      <Td>
                        {can("admin") ? (
                          <RowActions>
                            {/* An ended session keeps its control, disabled.
                                The table is mostly ended sessions, and a column
                                that is empty on those rows reads as missing
                                rather than as nothing left to do. */}
                            <ActionButton
                              icon={ActionIcon.revoke}
                              tone="danger"
                              disabledWhy={
                                session.status === "active"
                                  ? null
                                  : t("sessions.cannotRevoke")
                              }
                              onClick={() => setRevoking(session)}
                            >
                              {t("action.revoke")}
                            </ActionButton>
                          </RowActions>
                        ) : (
                          <NoRowActions reason={t("action.noPermission")} />
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Table>

            {sessions.data ? (
              <PageBar
                page={sessions.data.page}
                pages={sessions.data.pages}
                total={sessions.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <RevokeDialog
        session={revoking}
        onClose={() => setRevoking(null)}
        onDone={sessions.reload}
      />
    </div>
  );
}

function RevokeDialog({
  session,
  onClose,
  onDone,
}: {
  session: AdminSession | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setReason("");
    setError(null);
    onClose();
  }

  async function revokeOne() {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      await api.revokeSession(session.id, reason.trim() || undefined);
      toast.success(t("sessions.ended"), session.username);
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.endSession"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function revokeAll() {
    if (!session?.principalId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.revokeAllSessions(
        session.principalType,
        session.principalId,
        reason.trim() || undefined,
      );
      toast.success(
        t("sessions.endedCount", { count: result.sessionsEnded }),
        session.username,
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.endSessions"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(session)}
      onClose={close}
      eyebrow={t("sessions.eyebrow")}
      title={t("sessions.revokeTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button variant="secondary" onClick={revokeAll} loading={busy}>
            {t("sessions.revokeAll")}
          </Button>
          <Button variant="danger" onClick={revokeOne} loading={busy}>
            {t("action.revoke")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
          {t("sessions.revokeBody", { username: session?.username ?? "" })}
        </p>
        <TextField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          hint={t("common.reasonHint")}
          autoFocus
        />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}
